/**
 * ViewModeContext — Tracks whether the app is in StreamRift Views mode
 *
 * Provides isViewsMode flag and the active view ID.
 * Used by AppSidebar for glow indicator and by App for routing.
 */

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

interface ViewModeContextValue {
  /** True when viewing a StreamRift View (read-only mode) */
  isViewsMode: boolean;
  /** Currently active view config ID, null when in production mode */
  activeViewId: string | null;
  /** Enter views mode with a specific view */
  enterViewsMode: (viewId: string) => void;
  /** Return to production mode */
  exitViewsMode: () => void;
  /** Whether the gallery slide-out panel is open */
  isGalleryOpen: boolean;
  /** Toggle the gallery panel */
  setGalleryOpen: (open: boolean) => void;
}

const ViewModeContext = createContext<ViewModeContextValue | null>(null);

export function ViewModeProvider({ children }: { children: ReactNode }) {
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [isGalleryOpen, setGalleryOpen] = useState(false);

  const enterViewsMode = useCallback((viewId: string) => {
    setActiveViewId(viewId);
  }, []);

  const exitViewsMode = useCallback(() => {
    setActiveViewId(null);
    setGalleryOpen(false);
  }, []);

  const isViewsMode = activeViewId !== null;

  return (
    <ViewModeContext.Provider
      value={{
        isViewsMode,
        activeViewId,
        enterViewsMode,
        exitViewsMode,
        isGalleryOpen,
        setGalleryOpen,
      }}
    >
      {children}
    </ViewModeContext.Provider>
  );
}

export function useViewMode(): ViewModeContextValue {
  const ctx = useContext(ViewModeContext);
  if (!ctx) {
    throw new Error('useViewMode must be used within <ViewModeProvider>');
  }
  return ctx;
}
